import { Component, ElementRef, NgZone, OnDestroy, signal, viewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  EventStateEnum,
  IEventInfoResourceResponseBm,
  IResourceListItem,
  OutlookReservationMode,
  ResourceReservationStatusType,
  ResourceUnavailabilityReason,
  TouchOneHttpService,
} from '@outlook-addin/cue-http';
import * as fromModule from '../../ngrx/reducers';
import { fromEvent, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { IReservationDetail } from '@outlook-addin/cue-http';
import { translation } from '@cue/translations';
import { cueClose, cueRefresh, IconComponent } from '@cue/ui/icons';
import { refreshReservationDetail } from '../../ngrx/actions/reservation.actions';
import { ReservationActions } from '../../ngrx/actions';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocationDetailComponent } from '../location-detail/location-detail.component';
import { OrderDetailComponent } from '../order-detail/order-detail.component';
import { Pack } from '@cue/data';
import { AsyncPipe, NgStyle } from '@angular/common';
import { explicitEffect } from 'ngxtension/explicit-effect';
import {
  AddinConfigService,
  getResourceAvailabilityRestrictionsMode,
  ResourceAvailabilityRestrictionsModeId,
} from '@outlook-addin/shared';
import { ResourceAssignType } from '@cue/api';
import { ResourceAvailabilityComponent } from '../../resource-availability/resource-availability.component';
import AppointmentCompose = Office.AppointmentCompose;
import EmailUser = Office.EmailUser;
import LocationDetails = Office.LocationDetails;

type ResourceRemovalNotification = {
  id: number;
  messageKey: string;
  resources: string;
  resourceDetails: ResourceRemovalNotificationResource[];
  start: Date;
  end: Date;
  timeoutMs: number;
  confirmation?: ResourceRemovalNotificationConfirmation;
};

type ResourceRemovalNotificationResource = {
  displayName: string;
  resource: IResourceListItem;
};

type ResourceRemovalNotificationConfirmation = {
  primaryAction: () => void;
  secondaryAction: () => void;
  primaryLabelKey?: string;
  secondaryLabelKey?: string;
  timeoutMs: number;
};

type CurrentOutlookReservationSnapshot = {
  locations: LocationDetails[];
  recipients: EmailUser[];
  start: Date | null;
  end: Date | null;
};

type ReservationOriginalRange = {
  start: Date | null;
  end: Date | null;
  startLocalDateTime: string | null;
  endLocalDateTime: string | null;
  startTimeZone: string | null;
  endTimeZone: string | null;
};

type EventInfoAttendeeStatus = {
  response: string;
  time: string;
};

@Component({
  selector: 'addin-reservation-container',
  templateUrl: './reservation-container.component.html',
  styleUrls: ['./reservation-container.component.scss'],
  imports: [
    TranslocoPipe,
    LocationDetailComponent,
    OrderDetailComponent,
    IconComponent,
    AsyncPipe,
    NgStyle,
    ResourceAvailabilityComponent,
  ],
})
export class ReservationContainerComponent implements OnDestroy {
  ICONS = {
    cueClose,
    cueRefresh,
  };
  protected readonly translation = translation;
  protected eventStateEnum = EventStateEnum;
  protected readonly resourceAssignType = ResourceAssignType;
  protected readonly notificationTimeoutMs = 10000;
  protected readonly resourceRestrictionConfirmationTimeoutMs = 20000;
  resourceRemovalNotifications = signal<ResourceRemovalNotification[]>([]);

  resetListener$ = new Subject<void>();
  tabs = viewChildren<ElementRef<HTMLDivElement>>('tab');
  sliders = viewChildren<ElementRef<HTMLDivElement>>('slider');
  boundSlidersWhenChanges = explicitEffect([this.sliders], () => {
    this.boundSlider();
  });

  activeTabIndex = 0;
  fromTab = false;
  hrStyle = signal({
    left: 0,
    width: 0,
  });

  subscriptionList: Subscription[] = [];
  dataPack$: Observable<Pack<IReservationDetail>>;
  removedResourceIds = signal<Set<number>>(new Set());
  private currentReservationDetail: IReservationDetail | null = null;
  private reservationBaselineKey: string | null = null;
  private reservationOriginalRange: ReservationOriginalRange | null = null;
  private outlookLocationResourceIds: Set<number> | null = null;
  private outlookRecipientResourceIds: Set<number> | null = null;
  private outlookLocationKeys: Set<string> | null = null;
  private outlookRecipientKeys: Set<string> | null = null;
  private readonly handleAppointmentTimeChangedBound = this.handleAppointmentTimeChanged.bind(this);
  private readonly handleEnhancedLocationsChangedBound = this.handleEnhancedLocationsChanged.bind(this);
  private readonly handleRecipientsChangedBound = this.handleRecipientsChanged.bind(this);
  private readonly appointmentTimeChangeValidation$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private notificationId = 0;
  private validateResourcesAfterDetailReload = false;
  private lastLoadedReservationRestrictionValidationKey: string | null = null;
  private currentResourceRestrictionValidationRangeKey: string | null = null;
  private reloadReservationDetailTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly resourceRemovalNotificationTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly resourceAvailabilityRestrictionsMode = getResourceAvailabilityRestrictionsMode(
    this.configService.value.configuration
  );
  private readonly shouldUseResourceAvailabilityRestrictions =
    this.resourceAvailabilityRestrictionsMode !== ResourceAvailabilityRestrictionsModeId.DoNotModify;
  private readonly shouldAskBeforeModifyingOutlookEvent =
    this.resourceAvailabilityRestrictionsMode === ResourceAvailabilityRestrictionsModeId.AskBeforeModifying;
  private readonly shouldAutomaticallyEnforceResourceAvailabilityRestrictions =
    this.resourceAvailabilityRestrictionsMode === ResourceAvailabilityRestrictionsModeId.AutomaticallyEnforce;
  private currentEventDetail: any | null = null;
  private dismissedResourceRemovalConfirmationResourceIds = new Set<number>();

  constructor(
    private store: Store<fromModule.State>,
    private zone: NgZone,
    private touchOneHttpService: TouchOneHttpService,
    private configService: AddinConfigService
  ) {
    this.dataPack$ = this.store.select(fromModule.selectReservationDetail);
    this.subscriptionList.push(
      this.store.select(fromModule.selectEventDetail).subscribe((eventDetail) => {
        this.currentEventDetail = eventDetail ?? null;
      })
    );
    this.subscriptionList.push(
      this.store.select(fromModule.selectHiddenReservationDetailResourceIds).subscribe((resourceIds) => {
        this.removedResourceIds.set(new Set(resourceIds));
      })
    );
    this.subscriptionList.push(
      this.store.select(fromModule.selectDismissedResourceRemovalConfirmationResourceIds).subscribe((resourceIds) => {
        this.dismissedResourceRemovalConfirmationResourceIds = new Set(resourceIds);
      })
    );
    this.subscriptionList.push(
      this.dataPack$.subscribe((pack) => {
        const previousReservationDetail = this.currentReservationDetail;
        if (pack.data) {
          const reservationDetail = pack.data;
          this.currentReservationDetail = reservationDetail;
          const baselineChanged = this.updateReservationOriginalRange(reservationDetail);
          const resourceSetChanged = this.hasReservationResourceSetChanged(
            previousReservationDetail,
            reservationDetail
          );
          const shouldSyncOutlookResources =
            baselineChanged ||
            resourceSetChanged ||
            !this.outlookLocationResourceIds ||
            !this.outlookRecipientResourceIds;

          let shouldValidateResources = false;
          if (this.validateResourcesAfterDetailReload) {
            if (previousReservationDetail !== reservationDetail) {
              this.validateResourcesAfterDetailReload = false;
              shouldValidateResources = true;
            }
          } else {
            shouldValidateResources = this.shouldValidateLoadedReservationDetailRestrictions(reservationDetail);
          }

          const validateAfterOutlookResourceSync = () => {
            if (shouldValidateResources) {
              this.validateReservationDetailResourcesAgainstCurrentTime(reservationDetail);
            }
          };

          if (shouldSyncOutlookResources) {
            void this.syncReservationDetailResourcesFromCurrentOutlookState().then(validateAfterOutlookResourceSync);
            return;
          }

          validateAfterOutlookResourceSync();
          return;
        }

        this.clearCurrentReservationDetailState();
      })
    );

    Office.onReady().then((x) => {
      if (Office.context.mailbox.item.itemType !== 'appointment') {
        this.zone.run(() => {
          this.store.dispatch(
            ReservationActions.outlookReservationModeChange({
              reservationMode: OutlookReservationMode.NotReservationDetail,
            })
          );
        });
      } else {
        this.registerAppointmentTimeChangedHandler();
        this.registerOutlookResourceChangedHandlers();
        void this.syncReservationDetailResourcesFromCurrentOutlookState();

        if (Office.context.mailbox.item.itemId) {
          this.zone.run(() => {
            this.store.dispatch(
              ReservationActions.outlookReservationModeChange({
                reservationMode: OutlookReservationMode.View,
              })
            );
          });
        } else {
          this.zone.run(() => {
            this.store.dispatch(
              ReservationActions.outlookReservationModeChange({
                reservationMode: OutlookReservationMode.Edit,
              })
            );
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.appointmentTimeChangeValidation$.next();
    this.appointmentTimeChangeValidation$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.resetListener$.next();
    this.resetListener$.complete();
    this.removeAppointmentTimeChangedHandler();
    this.removeOutlookResourceChangedHandlers();
    if (this.reloadReservationDetailTimeoutId) {
      clearTimeout(this.reloadReservationDetailTimeoutId);
    }
    this.resourceRemovalNotificationTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.resourceRemovalNotificationTimeouts.clear();
    this.subscriptionList.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  //TABS
  setActiveTab(index: number) {
    this.fromTab = true;
    this.activeTabIndex = index;
    const slider = this.sliders()[0];
    slider.nativeElement.scrollTo((slider.nativeElement.children[index] as HTMLElement).offsetLeft, 0);
  }

  boundSlider() {
    this.resetListener$.next();
    const slider = this.sliders()[0];
    if (!slider) {
      return;
    }
    let ticking = false;
    const tabs = this.tabs();
    const found = tabs[0];

    if (found && found.nativeElement.getClientRects().length > 0) {
      const relativeLeftToParent = found.nativeElement.offsetLeft;
      const width = found.nativeElement.getClientRects()[0].width + 12;
      this.hrStyle.set({
        left: relativeLeftToParent - 6,
        width: width,
      });
    }
    let lastKnownScrollPosition = 0;
    const recalculate = (scrollPos: number) => {
      let totalWidth = 0;
      const nativeElements = slider.nativeElement.children;

      for (let i = 0; i < nativeElements.length; i++) {
        if (totalWidth + nativeElements[i].clientWidth / 2 > scrollPos) {
          if (i !== this.activeTabIndex) {
            this.zone.run(() => {
              this.activeTabIndex = i;
              const tabs = this.tabs();
              const found = tabs[i];

              if (found) {
                const relativeLeftToParent = found.nativeElement.offsetLeft;
                const width = found.nativeElement.getClientRects()[0].width + 12;
                this.hrStyle.set({
                  left: relativeLeftToParent - 6,
                  width: width,
                });
              }
            });
          }
          break;
        }
        totalWidth += nativeElements[i].clientWidth;
      }
    };

    this.zone.runOutsideAngular(() => {
      fromEvent(slider.nativeElement, 'scroll')
        .pipe(takeUntil(this.resetListener$))
        .subscribe((e) => {
          lastKnownScrollPosition = slider.nativeElement.scrollLeft;

          if (!ticking) {
            window.requestAnimationFrame(() => {
              recalculate(lastKnownScrollPosition);
              ticking = false;
            });

            ticking = true;
          }
        });
    });
  }

  refresh() {
    this.store.dispatch(refreshReservationDetail());
  }

  closeResourceRemovalNotification(notificationId: number) {
    const timeoutId = this.resourceRemovalNotificationTimeouts.get(notificationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.resourceRemovalNotificationTimeouts.delete(notificationId);
    }

    this.resourceRemovalNotifications.update((notifications) =>
      notifications.filter((notification) => notification.id !== notificationId)
    );
  }

  confirmResourceRemovalNotification(notificationId: number) {
    const notification = this.resourceRemovalNotifications().find((item) => item.id === notificationId);
    if (!notification) {
      return;
    }

    this.closeResourceRemovalNotification(notificationId);
    notification.confirmation?.primaryAction();
  }

  cancelResourceRemovalNotification(notificationId: number) {
    const notification = this.resourceRemovalNotifications().find((item) => item.id === notificationId);
    if (!notification) {
      return;
    }

    this.closeResourceRemovalNotification(notificationId);
    notification.confirmation?.secondaryAction();
  }

  getVisibleResources(resources: IEventInfoResourceResponseBm[]): IEventInfoResourceResponseBm[] {
    const removedResourceIds = this.removedResourceIds();
    if (removedResourceIds.size === 0) {
      return resources;
    }

    return resources.filter((resource) => !removedResourceIds.has(resource.id));
  }

  private hasReservationResourceSetChanged(
    previousReservationDetail: IReservationDetail | null,
    currentReservationDetail: IReservationDetail
  ): boolean {
    if (!previousReservationDetail) {
      return false;
    }

    return (
      this.getReservationResourceIdKey(previousReservationDetail.resources ?? []) !==
      this.getReservationResourceIdKey(currentReservationDetail.resources ?? [])
    );
  }

  private shouldValidateLoadedReservationDetailRestrictions(reservationDetail: IReservationDetail): boolean {
    if (!this.shouldUseResourceAvailabilityRestrictions || !this.getReservationBaselineKey(reservationDetail)) {
      return false;
    }

    const validationKey = this.getLoadedReservationRestrictionValidationKey(reservationDetail);
    if (!validationKey || this.lastLoadedReservationRestrictionValidationKey === validationKey) {
      return false;
    }

    this.lastLoadedReservationRestrictionValidationKey = validationKey;
    return true;
  }

  private getLoadedReservationRestrictionValidationKey(reservationDetail: IReservationDetail): string | null {
    const eventKey = this.getReservationBaselineKey(reservationDetail);
    const start = this.parseReservationDate(reservationDetail.start);
    const end = this.parseReservationDate(reservationDetail.end);
    const resourceKey = this.getReservationResourceIdKey(reservationDetail.resources ?? []);
    if (!eventKey || !start || !end || !resourceKey) {
      return null;
    }

    return [eventKey, start.toISOString(), end.toISOString(), resourceKey].join('|');
  }

  private getReservationResourceIdKey(resources: IEventInfoResourceResponseBm[]): string {
    return resources
      .map((resource) => resource.id)
      .sort((a, b) => a - b)
      .join(',');
  }

  private clearCurrentReservationDetailState() {
    this.currentReservationDetail = null;
    this.reservationBaselineKey = null;
    this.reservationOriginalRange = null;
    this.outlookLocationResourceIds = null;
    this.outlookRecipientResourceIds = null;
    this.outlookLocationKeys = null;
    this.outlookRecipientKeys = null;
    this.validateResourcesAfterDetailReload = false;
    this.lastLoadedReservationRestrictionValidationKey = null;
    this.currentResourceRestrictionValidationRangeKey = null;
  }

  private registerAppointmentTimeChangedHandler() {
    if (!this.shouldUseResourceAvailabilityRestrictions) {
      return;
    }

    const item = Office.context.mailbox.item as AppointmentCompose;
    item.addHandlerAsync?.(Office.EventType.AppointmentTimeChanged, this.handleAppointmentTimeChangedBound);
  }

  private removeAppointmentTimeChangedHandler() {
    if (!Office.context.mailbox || Office.context.mailbox.item.itemType !== 'appointment') {
      return;
    }

    const item = Office.context.mailbox.item as AppointmentCompose;
    item.removeHandlerAsync?.(Office.EventType.AppointmentTimeChanged, this.handleAppointmentTimeChangedBound);
  }

  private registerOutlookResourceChangedHandlers() {
    const item = Office.context.mailbox.item as AppointmentCompose;
    item.addHandlerAsync?.(Office.EventType.EnhancedLocationsChanged, this.handleEnhancedLocationsChangedBound);
    item.addHandlerAsync?.(Office.EventType.RecipientsChanged, this.handleRecipientsChangedBound);
  }

  private removeOutlookResourceChangedHandlers() {
    if (!Office.context.mailbox || Office.context.mailbox.item.itemType !== 'appointment') {
      return;
    }

    const item = Office.context.mailbox.item as AppointmentCompose;
    item.removeHandlerAsync?.(Office.EventType.EnhancedLocationsChanged, this.handleEnhancedLocationsChangedBound);
    item.removeHandlerAsync?.(Office.EventType.RecipientsChanged, this.handleRecipientsChangedBound);
  }

  private handleEnhancedLocationsChanged(eventArgs: { enhancedLocations?: LocationDetails[] }) {
    if (Array.isArray(eventArgs?.enhancedLocations)) {
      this.zone.run(() => this.syncReservationDetailResourcesFromOutlookLocations(eventArgs.enhancedLocations));
      return;
    }

    setTimeout(() => {
      this.getOutlookLocations((locations) => {
        if (Array.isArray(locations)) {
          this.zone.run(() => this.syncReservationDetailResourcesFromOutlookLocations(locations));
        }
      });
    }, 50);
  }

  private handleRecipientsChanged() {
    setTimeout(() => {
      this.getOutlookRecipientEmails((recipientEmails) => {
        if (!recipientEmails) {
          return;
        }

        this.zone.run(() => this.syncReservationDetailResourcesFromOutlookRecipients(recipientEmails));
      });
    }, 50);
  }

  private syncReservationDetailResourcesFromCurrentOutlookState(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.canReadOutlookResourceState()) {
        resolve();
        return;
      }

      this.getOutlookLocations((locations) => {
        if (!locations) {
          resolve();
          return;
        }

        this.getOutlookRecipientEmails((recipientEmails) => {
          if (!recipientEmails) {
            resolve();
            return;
          }

          this.zone.run(() => {
            this.syncReservationDetailResourcesFromOutlookSnapshot(locations, recipientEmails);
            resolve();
          });
        });
      });
    });
  }

  private canReadOutlookResourceState(): boolean {
    const item = Office.context.mailbox?.item as AppointmentCompose | undefined;

    return !!item?.enhancedLocation?.getAsync;
  }

  private syncReservationDetailResourcesFromOutlookSnapshot(locations: LocationDetails[], recipientEmails: string[]) {
    const resources = this.currentReservationDetail?.resources ?? [];
    this.outlookLocationKeys = new Set(this.getOutlookLocationKeys(locations));
    this.outlookRecipientKeys = new Set(this.getOutlookRecipientKeys(recipientEmails));

    if (resources.length === 0) {
      this.outlookLocationResourceIds = new Set();
      this.outlookRecipientResourceIds = new Set();
      return;
    }

    const resourceIdsInOutlook = new Set([
      ...this.getResourceIdsFromOutlookLocations(locations, resources),
      ...this.getResourceIdsFromOutlookRecipients(recipientEmails, resources),
    ]);

    const resourceIdsToHide = resources
      .filter((resource) => !resourceIdsInOutlook.has(resource.id))
      .map((resource) => resource.id);
    const resourceIdsToShow = resources
      .filter((resource) => resourceIdsInOutlook.has(resource.id))
      .map((resource) => resource.id);

    this.outlookLocationResourceIds = new Set(this.getResourceIdsFromOutlookLocations(locations, resources));
    this.outlookRecipientResourceIds = new Set(this.getResourceIdsFromOutlookRecipients(recipientEmails, resources));
    this.syncHiddenReservationDetailResources(resourceIdsToHide, resourceIdsToShow);
  }

  private syncReservationDetailResourcesFromOutlookLocations(locations: LocationDetails[]) {
    const resources = this.currentReservationDetail?.resources ?? [];
    const currentLocationKeys = new Set(this.getOutlookLocationKeys(locations));
    const previousResourceIds = this.outlookLocationResourceIds;
    const previousLocationKeys = this.outlookLocationKeys;

    if (resources.length === 0) {
      this.outlookLocationResourceIds = new Set();
      this.outlookLocationKeys = currentLocationKeys;
      if (this.hasAddedKeys(previousLocationKeys, currentLocationKeys)) {
        this.scheduleReservationDetailReloadFromCurrentOutlookState();
      }
      return;
    }

    const resourceIdsInLocations = new Set(this.getResourceIdsFromOutlookLocations(locations, resources));
    this.outlookLocationResourceIds = resourceIdsInLocations;
    this.outlookLocationKeys = currentLocationKeys;
    if (!previousResourceIds) {
      if (this.hasAddedKeys(previousLocationKeys, currentLocationKeys)) {
        this.scheduleReservationDetailReloadFromCurrentOutlookState();
      }
      return;
    }

    const resourceIdsToHide = Array.from(previousResourceIds).filter(
      (resourceId) => !resourceIdsInLocations.has(resourceId)
    );
    const resourceIdsToShow = Array.from(resourceIdsInLocations).filter(
      (resourceId) => !previousResourceIds.has(resourceId)
    );
    this.hideRemovedResources(resourceIdsToHide);
    this.showReservationDetailResources(resourceIdsToShow);

    const resourcesToRemoveFromRecipients = resources.filter((resource) => resourceIdsToHide.includes(resource.id));
    this.removeResourceRecipientsFromOutlookEvent(resourcesToRemoveFromRecipients);

    if (this.hasAddedKeys(previousLocationKeys, currentLocationKeys)) {
      this.scheduleReservationDetailReloadFromCurrentOutlookState();
    }
  }

  private syncReservationDetailResourcesFromOutlookRecipients(recipientEmails: string[]) {
    const resources = this.currentReservationDetail?.resources ?? [];
    const currentRecipientKeys = new Set(this.getOutlookRecipientKeys(recipientEmails));
    const previousResourceIds = this.outlookRecipientResourceIds;
    const previousRecipientKeys = this.outlookRecipientKeys;

    if (resources.length === 0) {
      this.outlookRecipientResourceIds = new Set();
      this.outlookRecipientKeys = currentRecipientKeys;
      if (this.hasAddedKeys(previousRecipientKeys, currentRecipientKeys)) {
        this.scheduleReservationDetailReloadFromCurrentOutlookState();
      }
      return;
    }

    const resourceIdsInRecipients = new Set(this.getResourceIdsFromOutlookRecipients(recipientEmails, resources));
    this.outlookRecipientResourceIds = resourceIdsInRecipients;
    this.outlookRecipientKeys = currentRecipientKeys;
    if (!previousResourceIds) {
      if (this.hasAddedKeys(previousRecipientKeys, currentRecipientKeys)) {
        this.scheduleReservationDetailReloadFromCurrentOutlookState();
      }
      return;
    }

    const resourceIdsToHide = Array.from(previousResourceIds).filter(
      (resourceId) => !resourceIdsInRecipients.has(resourceId)
    );
    const resourceIdsToShow = Array.from(resourceIdsInRecipients).filter(
      (resourceId) => !previousResourceIds.has(resourceId)
    );
    this.hideRemovedResources(resourceIdsToHide);
    this.showReservationDetailResources(resourceIdsToShow);

    const resourcesToRemoveFromLocations = resources.filter((resource) => resourceIdsToHide.includes(resource.id));
    this.removeResourceLocationsFromOutlookEvent(resourcesToRemoveFromLocations);

    if (this.hasAddedKeys(previousRecipientKeys, currentRecipientKeys)) {
      this.scheduleReservationDetailReloadFromCurrentOutlookState();
    }
  }

  private syncHiddenReservationDetailResources(resourceIdsToHide: number[], resourceIdsToShow: number[]) {
    if (resourceIdsToHide.length > 0) {
      this.hideRemovedResources(resourceIdsToHide);
    }

    if (resourceIdsToShow.length > 0) {
      this.showReservationDetailResources(resourceIdsToShow);
    }
  }

  private scheduleReservationDetailReloadFromCurrentOutlookState() {
    if (!this.getReservationDetailRequestSource()) {
      return;
    }

    if (this.reloadReservationDetailTimeoutId) {
      clearTimeout(this.reloadReservationDetailTimeoutId);
    }

    this.reloadReservationDetailTimeoutId = setTimeout(() => {
      this.reloadReservationDetailTimeoutId = null;
      this.reloadReservationDetailFromCurrentOutlookState();
    }, 100);
  }

  private reloadReservationDetailFromCurrentOutlookState() {
    const requestSource = this.getReservationDetailRequestSource();
    if (!requestSource) {
      return;
    }

    this.getCurrentOutlookReservationSnapshot((snapshot) => {
      if (!snapshot) {
        return;
      }

      this.validateResourcesAfterDetailReload = this.shouldUseResourceAvailabilityRestrictions;
      this.zone.run(() => {
        this.store.dispatch(
          ReservationActions.loadReservationDetail({
            eventDetail: this.createReservationDetailRequest(requestSource, snapshot),
            preserveCurrentDetail: true,
          })
        );
      });
    });
  }

  private getCurrentOutlookReservationSnapshot(callback: (snapshot: CurrentOutlookReservationSnapshot | null) => void) {
    const item = Office.context.mailbox?.item as AppointmentCompose | undefined;
    if (!item) {
      callback(null);
      return;
    }

    let pendingReads = 3;
    let callbackCalled = false;
    let currentLocations: LocationDetails[] | null = null;
    let currentRecipients: EmailUser[] | null = null;
    let currentStart: Date | null = null;
    let currentEnd: Date | null = null;

    const completeRead = () => {
      pendingReads--;
      if (pendingReads > 0 || callbackCalled) {
        return;
      }

      callbackCalled = true;
      if (!currentLocations || !currentRecipients) {
        callback(null);
        return;
      }

      callback({
        locations: currentLocations,
        recipients: currentRecipients,
        start: currentStart,
        end: currentEnd,
      });
    };

    this.getOutlookLocations((locations) => {
      currentLocations = locations;
      completeRead();
    });

    this.getOutlookRecipients((recipients) => {
      currentRecipients = recipients;
      completeRead();
    });

    this.getOutlookEventDates(item, (start, end) => {
      currentStart = start;
      currentEnd = end;
      completeRead();
    });
  }

  private getReservationDetailRequestSource(): any | null {
    return this.currentReservationDetail ?? this.currentEventDetail;
  }

  private createReservationDetailRequest(requestSource: any, snapshot: CurrentOutlookReservationSnapshot) {
    const locations = snapshot.locations.map((location) => this.createEventInfoLocation(location));
    const attendeeStatusByKey = this.createEventInfoAttendeeStatusByKey(requestSource);
    const iCalUid = requestSource.iCalUid ?? requestSource.iCalUId ?? '';
    const bodyContent =
      typeof requestSource.body === 'string' ? requestSource.body : (requestSource.body?.content ?? '');
    const start = requestSource.start;
    const end = requestSource.end;

    return {
      id: requestSource.id ?? '',
      iCalUid,
      iCalUId: iCalUid,
      showAs: requestSource.showAs ?? 'busy',
      subject: requestSource.subject ?? '',
      start: this.createEventInfoDate(snapshot.start, start),
      end: this.createEventInfoDate(snapshot.end, end),
      isAllDay: !!requestSource.isAllDay,
      isCancelled: !!(requestSource.cancelled ?? requestSource.isCancelled),
      location: locations[0] ?? this.createEmptyEventInfoLocation(),
      locations,
      attendees: snapshot.recipients.map((recipient) => this.createEventInfoAttendee(recipient, attendeeStatusByKey)),
      body: {
        contentType: 'html',
        content: bodyContent,
      },
      originalStartTimeZone: requestSource.originalStartTimeZone ?? start?.timeZone ?? 'UTC',
      originalEndTimeZone: requestSource.originalEndTimeZone ?? end?.timeZone ?? 'UTC',
      onlineMeetingProvider:
        requestSource.onlineMeetingProvider ?? (requestSource.teams ? 'teamsForBusiness' : 'unknown'),
      onlineMeeting:
        requestSource.onlineMeeting ?? (requestSource.teamsUrl ? { joinUrl: requestSource.teamsUrl } : null),
    };
  }

  private createEventInfoDate(date: Date | null, fallback: IReservationDetail['start'] | undefined) {
    const timeZone = fallback?.timeZone ?? 'UTC';

    return {
      dateTime: date
        ? this.formatEventInfoDate(date, timeZone)
        : (fallback?.dateTime ?? this.formatEventInfoDate(new Date(), timeZone)),
      timeZone,
    };
  }

  private formatEventInfoDate(date: Date, timeZone: string): string {
    const useUtc = timeZone.toUpperCase() === 'UTC';
    const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
    const month = (useUtc ? date.getUTCMonth() : date.getMonth()) + 1;
    const day = useUtc ? date.getUTCDate() : date.getDate();
    const hours = useUtc ? date.getUTCHours() : date.getHours();
    const minutes = useUtc ? date.getUTCMinutes() : date.getMinutes();
    const seconds = useUtc ? date.getUTCSeconds() : date.getSeconds();

    return `${this.padDatePart(year, 4)}-${this.padDatePart(month)}-${this.padDatePart(day)}T${this.padDatePart(hours)}:${this.padDatePart(minutes)}:${this.padDatePart(seconds)}`;
  }

  private padDatePart(value: number, length = 2): string {
    return value.toString().padStart(length, '0');
  }

  private createEventInfoLocation(location: LocationDetails) {
    const locationId = location.locationIdentifier?.id ?? location.emailAddress ?? location.displayName ?? '';
    const locationType = String(location.locationIdentifier?.type ?? '');

    return {
      displayName: location.displayName ?? '',
      locationUri: location.emailAddress ?? locationId,
      locationType,
      uniqueId: locationId,
      uniqueIdType: locationType,
    };
  }

  private createEmptyEventInfoLocation() {
    return {
      displayName: '',
      locationUri: '',
      locationType: '',
      uniqueId: '',
      uniqueIdType: '',
    };
  }

  private createEventInfoAttendee(recipient: EmailUser, attendeeStatusByKey: Map<string, EventInfoAttendeeStatus>) {
    const address = recipient.emailAddress ?? '';
    const name = recipient.displayName ?? address;

    return {
      type: 'required',
      status:
        this.getEventInfoAttendeeStatus([address], attendeeStatusByKey) ??
        this.getEventInfoAttendeeStatus([name], attendeeStatusByKey) ??
        this.createDefaultEventInfoAttendeeStatus(),
      emailAddress: {
        name,
        address,
      },
    };
  }

  private createEventInfoAttendeeStatusByKey(requestSource: any): Map<string, EventInfoAttendeeStatus> {
    const attendeeStatusByKey = new Map<string, EventInfoAttendeeStatus>();
    const addStatus = (keys: unknown[], value: unknown) => {
      const status = this.createEventInfoAttendeeStatus(value);
      if (!status) {
        return;
      }

      this.getUniqueNormalizedValues(keys).forEach((key) => attendeeStatusByKey.set(key, status));
    };

    (requestSource.attendees ?? []).forEach((attendee: any) => {
      addStatus(
        [attendee.emailAddress?.address, attendee.emailAddress, attendee.displayName],
        attendee.status ?? attendee.responseStatus
      );
    });

    (requestSource.resources ?? []).forEach((resource: IEventInfoResourceResponseBm) => {
      addStatus(
        [resource.email, resource.username, resource.displayName, resource.resourceDisplayNameForApp],
        resource.responseStatus
      );
    });

    return attendeeStatusByKey;
  }

  private createEventInfoAttendeeStatus(value: unknown): EventInfoAttendeeStatus | null {
    if (typeof value === 'string') {
      return {
        response: value,
        time: this.createDefaultEventInfoAttendeeStatus().time,
      };
    }

    if (!value || typeof value !== 'object') {
      return null;
    }

    const status = value as { response?: unknown; time?: unknown };
    if (typeof status.response !== 'string' || !status.response) {
      return null;
    }

    return {
      response: status.response,
      time: this.createEventInfoAttendeeStatusTime(status.time),
    };
  }

  private getEventInfoAttendeeStatus(
    keys: unknown[],
    attendeeStatusByKey: Map<string, EventInfoAttendeeStatus>
  ): EventInfoAttendeeStatus | null {
    for (const key of this.getUniqueNormalizedValues(keys)) {
      const status = attendeeStatusByKey.get(key);
      if (status) {
        return status;
      }
    }

    return null;
  }

  private createDefaultEventInfoAttendeeStatus(): EventInfoAttendeeStatus {
    return {
      response: 'none',
      time: '1970-01-01T00:00:00.000Z',
    };
  }

  private createEventInfoAttendeeStatusTime(value: unknown): string {
    if (value instanceof Date) {
      return value.toISOString();
    }

    return typeof value === 'string' && value ? value : this.createDefaultEventInfoAttendeeStatus().time;
  }

  private getOutlookEventDates(item: AppointmentCompose, callback: (start: Date | null, end: Date | null) => void) {
    if (!item.start?.getAsync || !item.end?.getAsync) {
      callback(null, null);
      return;
    }

    item.start?.getAsync((startResult) => {
      item.end?.getAsync((endResult) => {
        const start =
          startResult.status === Office.AsyncResultStatus.Succeeded ? this.parseEventDate(startResult.value) : null;
        const end =
          endResult.status === Office.AsyncResultStatus.Succeeded ? this.parseEventDate(endResult.value) : null;

        callback(start, end);
      });
    });
  }

  private handleAppointmentTimeChanged(eventArgs: { start?: string | Date; end?: string | Date }) {
    const reservationDetail = this.currentReservationDetail;
    if (!reservationDetail?.resources?.length || !eventArgs.start || !eventArgs.end) {
      return;
    }

    this.clearDismissedResourceRemovalConfirmations();
    const resources = this.getVisibleResources(reservationDetail.resources);
    if (resources.length === 0) {
      return;
    }

    const start = this.parseEventDate(eventArgs.start);
    const end = this.parseEventDate(eventArgs.end);
    if (!start || !end) {
      return;
    }

    this.validateReservationDetailResources(resources, reservationDetail, start, end);
  }

  private validateReservationDetailResourcesAgainstCurrentTime(reservationDetail: IReservationDetail) {
    if (!this.shouldUseResourceAvailabilityRestrictions) {
      return;
    }

    const resources = this.getVisibleResources(reservationDetail.resources ?? []);
    if (resources.length === 0) {
      return;
    }

    const start = this.parseReservationDate(reservationDetail.start);
    const end = this.parseReservationDate(reservationDetail.end);
    if (!start || !end) {
      return;
    }

    this.validateReservationDetailResources(resources, reservationDetail, start, end);
  }

  private validateReservationDetailResources(
    resources: IEventInfoResourceResponseBm[],
    reservationDetail: IReservationDetail,
    start: Date,
    end: Date
  ) {
    this.appointmentTimeChangeValidation$.next();
    const validationRangeKey = this.getResourceRemovalRangeKey(start, end);
    this.currentResourceRestrictionValidationRangeKey = validationRangeKey;
    this.cancelStaleResourceRemovalConfirmations(validationRangeKey);

    if (this.isEventEndInPast(end)) {
      this.removeInvalidResourcesForPastEventEnd(resources, start, end);
      return;
    }

    const currentEventResourceEmails = this.getCurrentEventResourceEmails(resources);
    const requirements = this.getResourceValidationRequirements(resources);

    const originalRange = this.getReservationOriginalRange(reservationDetail);

    this.touchOneHttpService
      .loadResourceList({
        start,
        end,
        iCalUid: reservationDetail.iCalUid,
        currentEventId: reservationDetail.id,
        currentEventResourceEmails,
        currentEventStart: originalRange.start,
        currentEventEnd: originalRange.end,
        currentEventStartLocalDateTime: originalRange.startLocalDateTime,
        currentEventEndLocalDateTime: originalRange.endLocalDateTime,
        currentEventStartTimeZone: originalRange.startTimeZone,
        currentEventEndTimeZone: originalRange.endTimeZone,
        requirements,
      })
      .pipe(takeUntil(this.appointmentTimeChangeValidation$), takeUntil(this.destroy$))
      .subscribe((resourceList) => {
        if (resourceList.error || !resourceList.data?.length) {
          return;
        }

        const detailResourceIds = new Set(resources.map((resource) => resource.id));
        const invalidResources = resourceList.data.filter(
          (resource) =>
            detailResourceIds.has(resource.resourceId) &&
            !this.isResourceAvailableForEditedEvent(resource, reservationDetail)
        );

        if (invalidResources.length === 0) {
          return;
        }

        const invalidDetailResources = invalidResources
          .map((resource) => resources.find((item) => item.id === resource.resourceId))
          .filter((resource): resource is IEventInfoResourceResponseBm => !!resource);

        if (invalidDetailResources.length === 0) {
          return;
        }

        this.handleInvalidResourcesForEditedEvent(invalidDetailResources, invalidResources, resources, start, end);
      });
  }

  private removeInvalidResourcesForPastEventEnd(resources: IEventInfoResourceResponseBm[], start: Date, end: Date) {
    const invalidResources = resources.map((resource) => this.createPastEventResourceListItem(resource));
    this.handleInvalidResourcesForEditedEvent(resources, invalidResources, resources, start, end);
  }

  private handleInvalidResourcesForEditedEvent(
    invalidDetailResources: IEventInfoResourceResponseBm[],
    invalidResources: IResourceListItem[],
    detailResources: IEventInfoResourceResponseBm[],
    start: Date,
    end: Date
  ) {
    if (this.shouldAskBeforeModifyingOutlookEvent) {
      const invalidResourcesToConfirm = invalidResources.filter(
        (resource) => !this.hasDismissedResourceRemovalConfirmation(resource)
      );
      const invalidResourceIdsToConfirm = new Set(invalidResourcesToConfirm.map((resource) => resource.resourceId));
      const invalidDetailResourcesToConfirm = invalidDetailResources.filter((resource) =>
        invalidResourceIdsToConfirm.has(resource.id)
      );
      if (invalidResourcesToConfirm.length === 0 || invalidDetailResourcesToConfirm.length === 0) {
        return;
      }

      this.showResourceRemovalConfirmation(
        invalidDetailResourcesToConfirm,
        invalidResourcesToConfirm,
        detailResources,
        start,
        end
      );
      return;
    }

    if (!this.shouldAutomaticallyEnforceResourceAvailabilityRestrictions) {
      return;
    }

    this.removeInvalidResourcesFromOutlookEvent(invalidDetailResources, invalidResources, detailResources, start, end);
  }

  private removeInvalidResourcesFromOutlookEvent(
    invalidDetailResources: IEventInfoResourceResponseBm[],
    invalidResources: IResourceListItem[],
    detailResources: IEventInfoResourceResponseBm[],
    start: Date,
    end: Date
  ) {
    this.removeResourcesFromOutlookEvent(invalidDetailResources, (removedDetailResources) => {
      if (removedDetailResources.length === 0) {
        return;
      }

      const removedResourceIds = new Set(removedDetailResources.map((resource) => resource.id));
      const removedInvalidResources = invalidResources.filter((resource) =>
        removedResourceIds.has(resource.resourceId)
      );

      this.showResourceRemovalNotification(removedInvalidResources, detailResources, start, end);
      this.hideRemovedResources(removedDetailResources.map((resource) => resource.id));
      this.refresh();
    });
  }

  private hideRemovedResources(resourceIds: number[]) {
    if (resourceIds.length === 0) {
      return;
    }

    this.store.dispatch(
      ReservationActions.hideReservationDetailResources({
        resourceIds,
      })
    );
  }

  private showReservationDetailResources(resourceIds: number[]) {
    if (resourceIds.length === 0) {
      return;
    }

    this.store.dispatch(
      ReservationActions.showReservationDetailResources({
        resourceIds,
      })
    );
  }

  private showResourceRemovalNotification(
    invalidResources: IResourceListItem[],
    detailResources: IEventInfoResourceResponseBm[],
    start: Date,
    end: Date
  ) {
    const resourceDetails = invalidResources.map((resource) => {
      const detailResource = detailResources.find((item) => item.id === resource.resourceId);
      const displayName = detailResource?.displayName ?? resource.resourceUserName;

      return {
        displayName,
        resource,
      };
    });

    if (this.hasResourceRemovalNotification(resourceDetails, start, end)) {
      return;
    }

    const notificationId = ++this.notificationId;
    const notification: ResourceRemovalNotification = {
      id: notificationId,
      messageKey: this.translation.outlookAddin.resourcesRemovedDueRestrictions,
      resources: resourceDetails.map((resource) => resource.displayName).join(', '),
      resourceDetails,
      start,
      end,
      timeoutMs: this.notificationTimeoutMs,
    };

    this.addResourceRemovalNotification(notification);
  }

  private showResourceRemovalConfirmation(
    invalidDetailResources: IEventInfoResourceResponseBm[],
    invalidResources: IResourceListItem[],
    detailResources: IEventInfoResourceResponseBm[],
    start: Date,
    end: Date
  ) {
    invalidResources.forEach((resource) => {
      const invalidDetailResource = invalidDetailResources.find((item) => item.id === resource.resourceId);
      if (!invalidDetailResource) {
        return;
      }

      this.showSingleResourceRemovalConfirmation(invalidDetailResource, resource, detailResources, start, end);
    });
  }

  private showSingleResourceRemovalConfirmation(
    invalidDetailResource: IEventInfoResourceResponseBm,
    invalidResource: IResourceListItem,
    detailResources: IEventInfoResourceResponseBm[],
    start: Date,
    end: Date
  ) {
    const detailResource = detailResources.find((item) => item.id === invalidResource.resourceId);
    const displayName = detailResource?.displayName ?? invalidResource.resourceUserName;
    const isResourceTypeConflict = invalidResource.notAllowedMultipeResourceTypeAndResourceOccupied === true;
    const resourceDetails = [
      {
        displayName,
        resource: invalidResource,
      },
    ];

    if (this.hasResourceRemovalNotification(resourceDetails, start, end)) {
      return;
    }

    const notificationId = ++this.notificationId;
    const notification: ResourceRemovalNotification = {
      id: notificationId,
      messageKey: this.translation.outlookAddin.resourcesShouldBeRemovedDueRestrictions,
      resources: displayName,
      resourceDetails,
      start,
      end,
      timeoutMs: this.resourceRestrictionConfirmationTimeoutMs,
      confirmation: {
        primaryLabelKey: isResourceTypeConflict ? this.translation.outlookAddin.confirmationRemove : undefined,
        secondaryLabelKey: isResourceTypeConflict ? this.translation.outlookAddin.confirmationKeep : undefined,
        primaryAction: () => {
          if (this.currentResourceRestrictionValidationRangeKey !== this.getResourceRemovalRangeKey(start, end)) {
            return;
          }

          this.removeInvalidResourcesFromOutlookEvent(
            [invalidDetailResource],
            [invalidResource],
            detailResources,
            start,
            end
          );
        },
        secondaryAction: () => {
          if (this.currentResourceRestrictionValidationRangeKey === this.getResourceRemovalRangeKey(start, end)) {
            this.addDismissedResourceRemovalConfirmation(invalidResource);
          }
        },
        timeoutMs: this.resourceRestrictionConfirmationTimeoutMs,
      },
    };

    this.addResourceRemovalNotification(notification);
  }

  private addResourceRemovalNotification(notification: ResourceRemovalNotification) {
    this.resourceRemovalNotifications.update((notifications) => [notification, ...notifications]);

    const timeoutId = setTimeout(() => {
      if (notification.confirmation) {
        this.confirmResourceRemovalNotification(notification.id);
        return;
      }

      this.closeResourceRemovalNotification(notification.id);
    }, notification.timeoutMs);
    this.resourceRemovalNotificationTimeouts.set(notification.id, timeoutId);
  }

  private cancelStaleResourceRemovalConfirmations(currentRangeKey: string) {
    this.resourceRemovalNotifications()
      .filter(
        (notification) =>
          notification.confirmation &&
          this.getResourceRemovalRangeKey(notification.start, notification.end) !== currentRangeKey
      )
      .map((notification) => notification.id)
      .forEach((notificationId) => this.cancelResourceRemovalNotification(notificationId));
  }

  private hasResourceRemovalNotification(
    resourceDetails: ResourceRemovalNotificationResource[],
    start: Date,
    end: Date
  ): boolean {
    const notificationKey = this.getResourceRemovalNotificationKey(resourceDetails, start, end);

    return this.resourceRemovalNotifications().some(
      (notification) =>
        this.getResourceRemovalNotificationKey(notification.resourceDetails, notification.start, notification.end) ===
        notificationKey
    );
  }

  private addDismissedResourceRemovalConfirmation(resource: IResourceListItem) {
    this.store.dispatch(ReservationActions.dismissResourceRemovalConfirmation({ resourceId: resource.resourceId }));
  }

  private hasDismissedResourceRemovalConfirmation(resource: IResourceListItem): boolean {
    return this.dismissedResourceRemovalConfirmationResourceIds.has(resource.resourceId);
  }

  private clearDismissedResourceRemovalConfirmations() {
    this.store.dispatch(ReservationActions.clearDismissedResourceRemovalConfirmations());
  }

  private getResourceRemovalNotificationKey(
    resourceDetails: ResourceRemovalNotificationResource[],
    start: Date,
    end: Date
  ): string {
    const resourceIds = resourceDetails
      .map((detail) => detail.resource.resourceId)
      .sort((a, b) => a - b)
      .join(',');

    return [resourceIds, start.toISOString(), end.toISOString()].join('|');
  }

  private getResourceRemovalRangeKey(start: Date, end: Date): string {
    return [start.toISOString(), end.toISOString()].join('|');
  }

  private getCurrentEventResourceEmails(resources: IEventInfoResourceResponseBm[]): string[] {
    return this.getUniqueNormalizedValues(resources.flatMap((resource) => [resource.username, resource.email]));
  }

  private getResourceValidationRequirements(resources: IEventInfoResourceResponseBm[]) {
    return resources.map((resource) => ({
      id: crypto.randomUUID(),
      resourceTypeId: resource.resourceTypId,
      capacity: [null, null],
      areaIds: resource.areaId ? [resource.areaId] : [],
      filters: [],
    }));
  }

  private updateReservationOriginalRange(reservationDetail: IReservationDetail): boolean {
    const baselineKey = this.getReservationBaselineKey(reservationDetail);
    if (baselineKey && this.reservationBaselineKey === baselineKey && this.reservationOriginalRange) {
      return false;
    }

    this.outlookLocationResourceIds = null;
    this.outlookRecipientResourceIds = null;
    this.outlookLocationKeys = null;
    this.outlookRecipientKeys = null;
    this.reservationBaselineKey = baselineKey;
    this.reservationOriginalRange = {
      start: this.parseReservationDate(reservationDetail.start),
      end: this.parseReservationDate(reservationDetail.end),
      startLocalDateTime: this.getReservationDateTimeValue(reservationDetail.start),
      endLocalDateTime: this.getReservationDateTimeValue(reservationDetail.end),
      startTimeZone: reservationDetail.start?.timeZone ?? null,
      endTimeZone: reservationDetail.end?.timeZone ?? null,
    };
    return true;
  }

  private getReservationBaselineKey(reservationDetail: IReservationDetail): string | null {
    return reservationDetail.id ?? reservationDetail.iCalUid ?? null;
  }

  private getReservationOriginalRange(reservationDetail: IReservationDetail): ReservationOriginalRange {
    return {
      start: this.reservationOriginalRange?.start ?? this.parseReservationDate(reservationDetail.start),
      end: this.reservationOriginalRange?.end ?? this.parseReservationDate(reservationDetail.end),
      startLocalDateTime:
        this.reservationOriginalRange?.startLocalDateTime ?? this.getReservationDateTimeValue(reservationDetail.start),
      endLocalDateTime:
        this.reservationOriginalRange?.endLocalDateTime ?? this.getReservationDateTimeValue(reservationDetail.end),
      startTimeZone: this.reservationOriginalRange?.startTimeZone ?? reservationDetail.start?.timeZone ?? null,
      endTimeZone: this.reservationOriginalRange?.endTimeZone ?? reservationDetail.end?.timeZone ?? null,
    };
  }

  private createPastEventResourceListItem(resource: IEventInfoResourceResponseBm): IResourceListItem {
    return {
      requirementIds: [],
      resourceId: resource.id,
      resourceGuid: resource.guid,
      resourceDisplayName: null,
      resourceDisplayNameForApp: null,
      resourceUserName: resource.username || resource.email,
      locations: [],
      isAvailable: false,
      isAssignedTo: resource.isAssignedTo,
      timeLine: '',
      resourceDetailAssistUrl: '',
      availableOutsideWorkingHours: false,
      unavailabilityReason: ResourceUnavailabilityReason.EventEndsInPast,
      unavailabilityReasonValue: null,
      inCurrentEvent: true,
      status: [],
      assignTo: null,
    };
  }

  private parseEventDate(value: string | Date): Date | null {
    const date = value instanceof Date ? value : new Date(value);

    return Number.isFinite(date.getTime()) ? date : null;
  }

  private parseReservationDate(value: IReservationDetail['start'] | undefined): Date | null {
    if (!value?.dateTime) {
      return null;
    }

    if (
      typeof value.dateTime === 'string' &&
      value.timeZone?.toUpperCase() === 'UTC' &&
      !/[zZ]|[+-]\d{2}:\d{2}$/.test(value.dateTime)
    ) {
      return this.parseEventDate(`${value.dateTime}Z`);
    }

    return this.parseEventDate(value.dateTime);
  }

  private getReservationDateTimeValue(value: IReservationDetail['start'] | undefined): string | null {
    if (!value?.dateTime) {
      return null;
    }

    return value.dateTime instanceof Date ? value.dateTime.toISOString() : value.dateTime;
  }

  private isEventEndInPast(end: Date): boolean {
    return end.getTime() <= Date.now();
  }

  private isResourceAvailableForEditedEvent(
    resource: IResourceListItem,
    reservationDetail: IReservationDetail
  ): boolean {
    if (resource.notAllowedMultipeResourceTypeAndResourceOccupied === true) {
      return false;
    }

    return (
      resource.isAvailable ||
      this.isReservedInCurrentEvent(resource) ||
      this.shouldPreserveReservedResourceWithUnknownCurrentEvent(resource, reservationDetail) ||
      (!resource.unavailabilityReason &&
        resource.availableOutsideWorkingHours &&
        resource.timeLine.indexOf(ResourceReservationStatusType.Reserved) === -1)
    );
  }

  private isReservedInCurrentEvent(resource: IResourceListItem): boolean {
    return (
      !!resource.inCurrentEvent &&
      !resource.unavailabilityReason &&
      resource.timeLine.indexOf(ResourceReservationStatusType.Reserved) > -1
    );
  }

  private shouldPreserveReservedResourceWithUnknownCurrentEvent(
    resource: IResourceListItem,
    reservationDetail: IReservationDetail
  ): boolean {
    return (
      !reservationDetail.iCalUid &&
      !!resource.inCurrentEvent &&
      !resource.unavailabilityReason &&
      resource.timeLine.indexOf(ResourceReservationStatusType.Reserved) > -1
    );
  }

  private removeResourceFromOutlookEvent(resource: IEventInfoResourceResponseBm, callback: (success: boolean) => void) {
    this.removeResourceLocation(resource, (locationRemoved) => {
      this.removeResourceRecipient(resource, (recipientRemoved) => callback(locationRemoved && recipientRemoved));
    });
  }

  private removeResourcesFromOutlookEvent(
    resources: IEventInfoResourceResponseBm[],
    callback: (removedResources: IEventInfoResourceResponseBm[]) => void,
    removedResources: IEventInfoResourceResponseBm[] = []
  ) {
    const [resource, ...remainingResources] = resources;
    if (!resource) {
      callback(removedResources);
      return;
    }

    this.removeResourceFromOutlookEvent(resource, (success) => {
      this.removeResourcesFromOutlookEvent(
        remainingResources,
        callback,
        success ? [...removedResources, resource] : removedResources
      );
    });
  }

  private removeResourceRecipientsFromOutlookEvent(resources: IEventInfoResourceResponseBm[]) {
    const [resource, ...remainingResources] = resources;
    if (!resource) {
      return;
    }

    this.removeResourceRecipient(resource, () => this.removeResourceRecipientsFromOutlookEvent(remainingResources));
  }

  private removeResourceLocationsFromOutlookEvent(resources: IEventInfoResourceResponseBm[]) {
    const [resource, ...remainingResources] = resources;
    if (!resource) {
      return;
    }

    this.removeResourceLocation(resource, () => this.removeResourceLocationsFromOutlookEvent(remainingResources));
  }

  private removeResourceLocation(resource: IEventInfoResourceResponseBm, callback: (success: boolean) => void) {
    Office.context.mailbox.item.enhancedLocation.getAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        callback(false);
        return;
      }

      const locationIdentifiers = this.getLocationIdentifiersForResource(result.value ?? [], resource);

      if (locationIdentifiers.length === 0) {
        callback(true);
        return;
      }

      Office.context.mailbox.item.enhancedLocation.removeAsync(locationIdentifiers, (removeResult) =>
        callback(removeResult.status === Office.AsyncResultStatus.Succeeded)
      );
    });
  }

  private removeResourceRecipient(resource: IEventInfoResourceResponseBm, callback: (success: boolean) => void) {
    const item = Office.context.mailbox.item as AppointmentCompose;
    this.removeResourceRecipientFromList(item.requiredAttendees, resource, (requiredRemoved) => {
      this.removeResourceRecipientFromList(item.optionalAttendees, resource, (optionalRemoved) =>
        callback(requiredRemoved && optionalRemoved)
      );
    });
  }

  private removeResourceRecipientFromList(
    recipients: Office.Recipients,
    resource: IEventInfoResourceResponseBm,
    callback: (success: boolean) => void
  ) {
    if (!recipients) {
      callback(true);
      return;
    }

    recipients.getAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        callback(false);
        return;
      }

      const resourceKeys = new Set(this.getResourcePrimaryMatchKeys(resource));
      const currentRecipients = result.value ?? [];
      const nextRecipients = currentRecipients.filter(
        (recipient) => !resourceKeys.has(this.normalizeKey(recipient.emailAddress))
      );

      if (nextRecipients.length === currentRecipients.length) {
        callback(true);
        return;
      }

      recipients.setAsync(nextRecipients, (setResult) =>
        callback(setResult.status === Office.AsyncResultStatus.Succeeded)
      );
    });
  }

  private getLocationIdentifiersForResource(
    locations: LocationDetails[],
    resource: IEventInfoResourceResponseBm
  ): LocationDetails['locationIdentifier'][] {
    const resourcePrimaryKeys = new Set(this.getResourcePrimaryMatchKeys(resource));
    const primaryMatches = locations.filter((location) =>
      this.getLocationPrimaryMatchKeys(location).some((key) => resourcePrimaryKeys.has(key))
    );

    if (primaryMatches.length > 0) {
      return primaryMatches.map((location) => location.locationIdentifier);
    }

    const resourceDisplayKeys = new Set(this.getResourceDisplayMatchKeys(resource));
    const displayMatches = locations.filter((location) =>
      this.getLocationDisplayMatchKeys(location).some((key) => resourceDisplayKeys.has(key))
    );

    return displayMatches.length === 1 ? [displayMatches[0].locationIdentifier] : [];
  }

  private getOutlookLocations(callback: (locations: LocationDetails[] | null) => void) {
    const item = Office.context.mailbox?.item as AppointmentCompose | undefined;
    if (!item?.enhancedLocation?.getAsync) {
      callback(null);
      return;
    }

    item.enhancedLocation.getAsync((result) => {
      callback(result.status === Office.AsyncResultStatus.Succeeded ? (result.value ?? []) : null);
    });
  }

  private getOutlookRecipientEmails(callback: (emails: string[] | null) => void) {
    this.getOutlookRecipients((recipients) => {
      callback(recipients?.map((recipient) => recipient.emailAddress) ?? null);
    });
  }

  private getOutlookRecipients(callback: (recipients: EmailUser[] | null) => void) {
    const item = Office.context.mailbox.item as AppointmentCompose;
    this.getRecipientsFromList(item.requiredAttendees, (requiredAttendees) => {
      this.getRecipientsFromList(item.optionalAttendees, (optionalAttendees) => {
        if (!requiredAttendees || !optionalAttendees) {
          callback(null);
          return;
        }

        callback([...requiredAttendees, ...optionalAttendees]);
      });
    });
  }

  private getRecipientsFromList(recipients: Office.Recipients, callback: (recipients: EmailUser[] | null) => void) {
    if (!recipients) {
      callback([]);
      return;
    }

    recipients.getAsync((result) => {
      callback(result.status === Office.AsyncResultStatus.Succeeded ? (result.value ?? []) : null);
    });
  }

  private getResourceIdsFromOutlookLocations(
    locations: LocationDetails[],
    resources: IEventInfoResourceResponseBm[]
  ): number[] {
    const matchingResourceIds = locations.flatMap((location) =>
      this.getResourcesMatchingOutlookLocation(location, resources).map((resource) => resource.id)
    );

    return Array.from(new Set(matchingResourceIds));
  }

  private getResourcesMatchingOutlookLocation(
    location: LocationDetails,
    resources: IEventInfoResourceResponseBm[]
  ): IEventInfoResourceResponseBm[] {
    const locationIdentifierKeys = new Set(this.getLocationPrimaryMatchKeys(location));
    const identifierMatches = resources.filter((resource) =>
      this.getResourcePrimaryMatchKeys(resource).some((key) => locationIdentifierKeys.has(key))
    );

    if (identifierMatches.length > 0) {
      return identifierMatches;
    }

    // Outlook display text has no guaranteed shape, so use it only when it identifies one resource.
    return this.getLocationDisplayFallbackKeys(location).flatMap((displayKey) => {
      const fallbackMatches = resources.filter((resource) =>
        this.getResourceFallbackMatchKeys(resource).includes(displayKey)
      );

      return fallbackMatches.length === 1 ? fallbackMatches : [];
    });
  }

  private getLocationDisplayFallbackKeys(location: LocationDetails): string[] {
    return this.getUniqueNormalizedValues([
      ...this.getLocationDisplayMatchKeys(location),
      ...this.getLocationDisplayTokenMatchKeys(location),
    ]);
  }

  private getResourceFallbackMatchKeys(resource: IEventInfoResourceResponseBm): string[] {
    return this.getUniqueNormalizedValues([
      ...this.getResourcePrimaryMatchKeys(resource),
      ...this.getResourceDisplayMatchKeys(resource),
    ]);
  }

  private getResourceIdsFromOutlookRecipients(
    recipientEmails: string[],
    resources: IEventInfoResourceResponseBm[]
  ): number[] {
    const recipientEmailKeys = new Set(recipientEmails.map((email) => this.normalizeKey(email)));

    return resources
      .filter((resource) => this.getResourcePrimaryMatchKeys(resource).some((key) => recipientEmailKeys.has(key)))
      .map((resource) => resource.id);
  }

  private getOutlookLocationKeys(locations: LocationDetails[]): string[] {
    return this.getUniqueNormalizedValues(
      locations.flatMap((location) => [
        ...this.getLocationPrimaryMatchKeys(location),
        ...this.getLocationDisplayMatchKeys(location),
      ])
    );
  }

  private getOutlookRecipientKeys(recipientEmails: string[]): string[] {
    return this.getUniqueNormalizedValues(recipientEmails);
  }

  private hasAddedKeys(previousKeys: Set<string> | null, currentKeys: Set<string>): boolean {
    return previousKeys ? Array.from(currentKeys).some((key) => !previousKeys.has(key)) : currentKeys.size > 0;
  }

  private getResourcePrimaryMatchKeys(resource: IEventInfoResourceResponseBm): string[] {
    return this.getUniqueNormalizedValues([resource.username, resource.email]);
  }

  private getResourceDisplayMatchKeys(resource: IEventInfoResourceResponseBm): string[] {
    return this.getUniqueNormalizedValues([resource.displayName, resource.resourceDisplayNameForApp]);
  }

  private getLocationPrimaryMatchKeys(location: LocationDetails): string[] {
    return this.getUniqueNormalizedValues([location.emailAddress, location.locationIdentifier?.id]);
  }

  private getLocationDisplayMatchKeys(location: LocationDetails): string[] {
    return this.getUniqueNormalizedValues([location.displayName]);
  }

  private getLocationDisplayTokenMatchKeys(location: LocationDetails): string[] {
    return this.getUniqueNormalizedValues(
      typeof location.displayName === 'string' ? location.displayName.split(/[,;\n]+/) : []
    );
  }

  private getUniqueNormalizedValues(values: unknown[]): string[] {
    return Array.from(
      new Set(values.map((value) => this.normalizeKey(value)).filter((value): value is string => value.length > 0))
    );
  }

  private normalizeKey(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }
}
